import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { getAdminAreaByIdApi } from "@/api/adminArea/adminAreaApi";
import {
  optimizeSelectValue,
  useGetAnalysisConfigMutation,
  useGetConfigurationByID,
  useSaveConfigurationMutation
} from "@/api/configuration/useConfigurationAPI";
import { fetchDatasetStream } from "@/api/dataset/fetchDatasetStream";
import {
  useGetDatasetDataMutation,
  useMutationDatasetMetadata
} from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import SaveSuccessNotiContent from "@/components/spatialAnalysis/notification/saveConfigurationSuccess";
import { ConfigurationFormData } from "@/components/spatialAnalysis/saveConfigurationPopup";
import { MESSAGE } from "@/constants/message";
import { buildFilterFromResponse } from "@/helpers/filterHelper";
import { RootState } from "@/store";
import { setDatasetHistory } from "@/store/slice/datasetSlice";
import {
  addLinkedDatasetId,
  setGlobalAdminArea
} from "@/store/slice/globalFilterSlice";
import { setSpatialState } from "@/store/slice/spatialSlice";
import {
  EspatialAggregationType,
  SpatialAggregationType
} from "@/types/common";
import {
  DatasetData,
  DatasetDetailResponse,
  DatasetDetailSchemaResponse,
  DatasetHistory,
  DatasetIndices,
  DatasetMetadata
} from "@/types/dataset";
import {
  AnalysisConfigurationDetail,
  ConfigurationPayload,
  DatasetConfigAttributeResponse,
  DatasetConfigResponse,
  GridConfigResponse
} from "@/types/project";
import { CustomError } from "@/utils/errors";

import { useMap } from "../map/core/useMapContext";
import { useDataset } from "../map/useDataset";
import useHandleClearView from "../ui/useHandleClearView";
import useToast, { EToastType } from "../ui/useToast";
import {
  getDeletedDatasetsName,
  mapDatasetWeights,
  mapSpatialState
} from "../useAnalysisConfiguration";
import { useAppFeatures } from "../useAppFeatures";
import useConfigurationMappers from "./useConfigurationMapper";

type UseConfigurationProps = {
  onSaveConfigSuccess?: () => void;
  onSaveConfigError?: (error: CustomError) => void;
  onOpenConfigSuccess?: (
    configurationDetailData: AnalysisConfigurationDetail
  ) => void;
  onOpenConfigError?: (error: CustomError) => void;
  setDuplicateNameOnSaveConfig?: (value: boolean) => void;
  onOpenProjectFromToast?: () => void;
};

export const useConfiguration = ({
  onSaveConfigSuccess,
  onSaveConfigError,
  onOpenConfigSuccess,
  onOpenConfigError,
  onOpenProjectFromToast
}: UseConfigurationProps) => {
  const dispatch = useDispatch();
  const { isRefactorAddDatasetEnabled } = useAppFeatures();
  const {
    handleAddDataset,
    handleAddDeletedDataset,
    handleCreateDatasetHistory,
    generateDatasetData
  } = useDataset();
  const { showToast } = useToast();
  const {
    mappingAnalysisOutput,
    mappingAreaInput,
    mappingDataset,
    mappingAdmAreaIds
  } = useConfigurationMappers();
  const { handleCloseDetailView } = useHandleClearView();
  const { zoomLevel, addFeature } = useMap();
  const { data: userProfileData } = useGetUserProfileAPI();

  const spatial = useSelector((state: RootState) => state.spatial);

  const handleOpenProjectFromToast = (data: AnalysisConfigurationDetail) => {
    if (!data?.id) return;
    handleCloseDetailView();
    onOpenProjectFromToast?.();
  };

  const { mutate: mutateSaveConfig } = useSaveConfigurationMutation({
    onMutate: () => {
      toast.dismiss();
      showToast({
        type: EToastType.LOADING,
        message: MESSAGE.SAVING_NEW_PROJECT
      });
    },
    onSuccess: (data, payload) => {
      toast.dismiss();
      showToast({
        type: EToastType.SUCCESS,
        message: (
          <SaveSuccessNotiContent
            title="Configuration successfully saved"
            description="You can check you project by clicking link below"
            buttonLabel="Open Saved Configuration"
            onOpen={() => handleOpenProjectFromToast(data)}
          />
        ),
        autoClose: false
      });
      // Need title and description for export summary analysis
      dispatch(
        setSpatialState({
          ...spatial,
          isEdit: true,
          currentConfigId: data.id,
          title: payload.name,
          description: payload.description
        })
      );

      onSaveConfigSuccess?.();
    },
    onError: (error) => {
      showToast({
        type: EToastType.ERROR,
        message: MESSAGE.SAVE_CONFIGURATION_FAILED
      });
      onSaveConfigError?.(error);
    }
  });

  const { mutateAsync: getDatasetDataPost } = useGetDatasetDataMutation({
    timezone: userProfileData?.timezone
  });
  const { mutateAsync: getDatasetMetadata } = useMutationDatasetMetadata();

  /**
   * Handle saving the configuration.
   * @param basicInfo - The basic configuration form data.
   */
  const handleSaveConfiguration = (basicInfo: ConfigurationFormData) => {
    const payload: ConfigurationPayload = {
      ...basicInfo,
      adm_area_ids: mappingAdmAreaIds(),
      datasets: mappingDataset(),
      area_input: mappingAreaInput(),
      analysis_output: mappingAnalysisOutput()
    };
    mutateSaveConfig(payload);
  };

  const getAggregationConfig = (
    gridConfig: GridConfigResponse | null | undefined
  ) => {
    if (gridConfig) {
      return {
        aggregationType: gridConfig?.aggregation_type,
        resolution: gridConfig?.level
      };
    }
    return null;
  };

  const buildDatasetFilter = (
    attributes: DatasetConfigAttributeResponse[],
    schema: DatasetDetailSchemaResponse[]
  ) => {
    // Helper to compare attribute_code handling empty string vs undefined/null
    const isAttributeCodeMatch = (
      schemaCode: string | undefined | null,
      attrCode: string | undefined
    ) => {
      const normalizedSchema = schemaCode || "";
      const normalizedAttr = attrCode || "";
      return normalizedSchema === normalizedAttr;
    };

    const getPosition = (filterId: string, attributeCode: string = "") =>
      schema.find(
        (item) =>
          item.id === filterId &&
          isAttributeCodeMatch(item.attribute_code, attributeCode)
      )?.position;

    return attributes
      .filter((attr) => attr.filter?.criteria)
      .map((attr) => {
        // When saving, paired_key_attribute_id is used as the attributeId.
        // When opening, we need to find the schema item that has this paired_key_attribute_id
        // and use its actual id instead.
        const schemaItemByPairedKey = schema.find(
          (item) =>
            item.paired_key_attribute_id === attr.id &&
            isAttributeCodeMatch(item.attribute_code, attr.attribute_code)
        );

        // If not found by paired_key, fallback to direct id match
        const schemaItem =
          schemaItemByPairedKey ??
          schema.find(
            (item) =>
              item.id === attr.id &&
              isAttributeCodeMatch(item.attribute_code, attr.attribute_code)
          );

        // Use the schema item's actual id if found via paired_key lookup
        const resolvedAttributeId = schemaItemByPairedKey
          ? schemaItemByPairedKey.id
          : attr.id;

        return {
          ...buildFilterFromResponse(attr.filter!),
          ...(attr.attribute_code?.startsWith("adm_area_level_")
            ? {}
            : { attributeCode: attr.attribute_code ?? "" }),
          filterId: `${resolvedAttributeId}_${getPosition(resolvedAttributeId, attr.attribute_code)}`,
          filterLabel: schemaItem?.attribute_name ?? "",
          attributeId: resolvedAttributeId,
          paired_key_attribute_id: schemaItem?.paired_key_attribute_id ?? null
        };
      });
  };

  const getDatasetDataAndMetadata = async (
    reversedDatasets: DatasetConfigResponse[]
  ) => {
    const datasetsData: DatasetData[] = [];
    const datasetsDetail: DatasetMetadata[] = [];

    // Collecting all dataset data and metadata first before adding to the map to optimize performance
    // Get full schema without grid_config
    const datasetMetadataAndDataPromises = reversedDatasets.map(
      async (dataset) => {
        const currentDatasetMetadata = await getDatasetMetadata({
          id: dataset.id
        });

        const filters = buildDatasetFilter(
          dataset.attributes,
          currentDatasetMetadata?.schema
        );

        const res = await fetchDatasetStream({
          datasetId: dataset.id,
          filters,
          timezone: userProfileData?.timezone,
          h3Configuration: getAggregationConfig(dataset?.grid_config),
          generateDatasetData
        });
        return {
          metadata: currentDatasetMetadata,
          data: res?.rawDataset || null
        };
      }
    );

    const results = await Promise.all(datasetMetadataAndDataPromises);

    results.forEach((result) => {
      datasetsDetail.push(result.metadata);
      if (result.data) datasetsData.push(result.data);
    });

    return { datasetsData, datasetsDetail };
  };

  const getAttributeSchema = async (datasetMetadata: DatasetMetadata) => {
    // For H3 dataset with data source BVT, we need to get the aggregated schema
    if (
      datasetMetadata?.spatial_aggregation_type ===
        EspatialAggregationType.H3 &&
      datasetMetadata?.source === "bvt"
    ) {
      const aggregatedDatasetMetadata = await getDatasetMetadata({
        id: datasetMetadata?.id,
        h3Configuration: {
          aggregationType: "AVG"
        }
      });
      if (aggregatedDatasetMetadata?.schema) {
        return aggregatedDatasetMetadata?.schema;
      }
    }
    return datasetMetadata?.schema || [];
  };

  const handleDatasetOnOpenProject = async (
    datasets: DatasetConfigResponse[]
  ) => {
    const reversedDatasets = [...datasets].reverse();

    const { datasetsData, datasetsDetail } = await getDatasetDataAndMetadata(
      reversedDatasets.filter((dataset) => dataset.available)
    );

    const duplicatedDatasetCount = new Map<string, number>();

    // After all data and metadata are collected, add them to the map
    for (const [index, dataset] of reversedDatasets.entries()) {
      const count = duplicatedDatasetCount.get(dataset.id) || 0;
      duplicatedDatasetCount.set(dataset.id, count + 1);
      if (dataset.available) {
        const datasetMetadata: DatasetMetadata | undefined =
          datasetsDetail.find((d) => d.id === dataset.id);

        if (datasetMetadata) {
          const addedDataset = await handleAddDataset({
            datasetMetadata: {
              ...datasetMetadata, // full metadata schema
              display_name: dataset?.display_name ?? dataset.name
            },
            datasetData: datasetsData[index],
            datasetIndex: duplicatedDatasetCount.get(dataset.id),
            savedFilters: buildDatasetFilter(
              dataset.attributes,
              datasetMetadata?.schema
            ),
            attributesSchema: await getAttributeSchema(datasetMetadata), // filtered metadata schema for h3 dataset
            h3Configuration: getAggregationConfig(dataset?.grid_config),
            weightValues: mapDatasetWeights(dataset, datasetMetadata?.schema),
            styleConfig: {
              colorFamily: dataset?.color_scale,
              opacity: dataset?.opacity,
              selectedCategory: dataset?.color_based,
              labelConfig: dataset?.label_config,
              colorSteps: dataset?.color_steps,
              rankConfig: {
                isEnabled: dataset.label_config?.enable_ranking ?? false,
                maxRanking: dataset.label_config?.max_ranking ?? 10
              }
            }
          });
          if (dataset.is_global_admin_area) {
            dispatch(addLinkedDatasetId({ datasetId: addedDataset.data.id }));
          }
        }
      } else {
        const deletedDatasetMetadata: DatasetDetailResponse = {
          id: dataset.id,
          name: dataset.name ?? "",
          display_name: dataset.display_name ?? "",
          country_id: "id", // arbitrary default value
          source: "bvt", // arbitrary default value
          spatial_aggregation_type:
            dataset.spatial_aggregation_type as SpatialAggregationType,
          short_description: "",
          published: false,
          created_at: "",
          updated_at: "",
          available: dataset.available,
          total_rows: 0,
          schema: []
        };
        handleAddDeletedDataset(
          deletedDatasetMetadata,
          duplicatedDatasetCount.get(dataset.id) || 1
        );
      }
    }
    return { datasetsData, datasetsDetail };
  };

  const handleCatchmentDatasetOnOpenProject = async (
    catchmentDatasetId: string,
    catchmentDatasetAttributes: DatasetConfigAttributeResponse[]
  ) => {
    const catchmentFilter = catchmentDatasetAttributes
      .filter((attr) => attr.filter?.criteria)
      .map((attr) => ({
        ...buildFilterFromResponse(attr.filter!),
        filterId: attr.id.split("_")?.[0],
        attributeCode: attr?.attribute_code ?? ""
      }));
    return {
      catchmentDatasetDetail: await getDatasetMetadata({
        id: catchmentDatasetId
      }),
      catchmentDatasetData: await getDatasetDataPost({
        id: catchmentDatasetId,
        filter: catchmentFilter
      })
    };
  };

  const handleAdmAreaOnOpenProject = async (adm_area_id: string) => {
    const adminAreaDetail = await getAdminAreaByIdApi(adm_area_id, zoomLevel);
    return {
      adminAreaData: {
        selectValue: optimizeSelectValue(adminAreaDetail),
        geoData: {
          data: [adminAreaDetail]
        }
      }
    };
  };

  const { mutateAsync: mutateOpenConfig, data: configurationDetailData } =
    useGetConfigurationByID({
      onMutate: () => {
        toast.dismiss();
        showToast({
          type: EToastType.LOADING,
          message: MESSAGE.LOADING_CONFIGURATION
        });
      },
      onSuccess: async (configurationDetail) => {
        const globalAdminAreaIds = configurationDetail.adm_area_ids;
        const areaInput = configurationDetail.area_input;
        const catchmentDatasetId = areaInput?.catchment?.dataset?.id;
        const catchmentDatasetAttributes =
          areaInput?.catchment?.dataset?.attributes ?? [];
        const admAreaId = areaInput?.adm_area_id;

        const { datasetsData, datasetsDetail } =
          isRefactorAddDatasetEnabled && configurationDetail.datasets
            ? await handleDatasetOnOpenProject(configurationDetail.datasets)
            : {};

        const { catchmentDatasetData, catchmentDatasetDetail } =
          isRefactorAddDatasetEnabled && catchmentDatasetId
            ? await handleCatchmentDatasetOnOpenProject(
                catchmentDatasetId,
                catchmentDatasetAttributes
              )
            : {};

        const { adminAreaData } =
          isRefactorAddDatasetEnabled && admAreaId
            ? await handleAdmAreaOnOpenProject(admAreaId)
            : {};

        const { adminAreaData: globalAdminAreaData } = globalAdminAreaIds?.[0]
          ? await handleAdmAreaOnOpenProject(globalAdminAreaIds?.[0])
          : {};

        showToast({
          message: MESSAGE.OPEN_PROJECT_SUCCESS,
          type: EToastType.SUCCESS
        });

        const deletedDatasets = getDeletedDatasetsName(
          configurationDetail.datasets
        );
        showToast({
          message: deletedDatasets
            ? `Dataset "${deletedDatasets}" cannot be loaded, please check the dataset from data management`
            : MESSAGE.OPEN_PROJECT_SUCCESS,
          type: deletedDatasets ? EToastType.WARNING : EToastType.SUCCESS
        });

        onOpenConfigSuccess?.({
          ...configurationDetail,
          ...(datasetsData && { datasetsData }),
          ...(datasetsDetail && { datasetsDetail }),
          ...(catchmentDatasetData && { catchmentDatasetData }),
          ...(catchmentDatasetDetail && { catchmentDatasetDetail }),
          ...(adminAreaData && { adminAreaData }),
          ...(globalAdminAreaData && { globalAdminAreaData })
        });
      },
      onError: (error) => {
        console.error("Error fetching configuration:", error);
        showToast({
          type: EToastType.ERROR,
          message: MESSAGE.LOADING_CONFIGURATION_FAILED
        });
        onOpenConfigError?.(error);
      }
    });

  const { mutateAsync: mutateGetAnalysisConfig } = useGetAnalysisConfigMutation(
    {
      onSuccess: async (data, payload) => {
        const configurationDetail = JSON.parse(data.configuration);

        const globalAdminAreaIds = configurationDetail.adm_area_ids;
        const areaInput = configurationDetail.area_input;
        const catchmentDatasetId = areaInput?.catchment?.dataset?.id;
        const catchmentDatasetAttributes =
          areaInput?.catchment?.dataset?.attributes ?? [];
        const admAreaId = areaInput?.adm_area_id;
        const datasets = configurationDetail.datasets;
        const reversedDatasets = [...datasets].reverse();

        const datasetHistory: DatasetHistory = {
          datasets: {},
          rawDatasets: {},
          visualizationOrder: []
        };
        const datasetIndices: DatasetIndices = {};

        const { datasetsData, datasetsDetail } =
          await getDatasetDataAndMetadata(reversedDatasets);

        for (const [index, dataset] of reversedDatasets.entries()) {
          const datasetMetadata: DatasetMetadata | undefined =
            datasetsDetail.find((d) => d.id === dataset.id);

          if (datasetMetadata) {
            const newDataset = await handleCreateDatasetHistory({
              datasetMetadata: {
                ...datasetsDetail[index], // full metadata schema
                display_name: dataset?.display_name ?? dataset.name
              },
              datasetData: datasetsData[index],
              savedFilters: buildDatasetFilter(
                dataset.attributes,
                datasetsDetail[index].schema
              ),
              attributesSchema: await getAttributeSchema(datasetsDetail[index]), // filtered metadata schema for h3 dataset
              h3Configuration: getAggregationConfig(dataset?.grid_config),
              weightValues: mapDatasetWeights(
                dataset,
                datasetsDetail[index].schema
              ),
              datasetHistory,
              datasetIndices
            });
            if (dataset.is_global_admin_area) {
              dispatch(addLinkedDatasetId({ datasetId: newDataset.data.id }));
            }
          }
        }
        dispatch(setDatasetHistory(datasetHistory));

        const { catchmentDatasetData, catchmentDatasetDetail } =
          catchmentDatasetId
            ? await handleCatchmentDatasetOnOpenProject(
                catchmentDatasetId,
                catchmentDatasetAttributes
              )
            : {};

        const { adminAreaData } = admAreaId
          ? await handleAdmAreaOnOpenProject(admAreaId)
          : {};

        const { adminAreaData: globalAdminAreaData } = globalAdminAreaIds?.[0]
          ? await handleAdmAreaOnOpenProject(globalAdminAreaIds?.[0])
          : {};

        const configResult = {
          ...configurationDetail,
          ...(datasetsData && { datasetsData }),
          ...(datasetsDetail && { datasetsDetail }),
          ...(catchmentDatasetData && { catchmentDatasetData }),
          ...(catchmentDatasetDetail && { catchmentDatasetDetail }),
          ...(adminAreaData && { adminAreaData }),
          ...(globalAdminAreaData && { globalAdminAreaData })
        };

        const spatialData = mapSpatialState(
          configResult,
          addFeature,
          payload?.analysisId ?? spatial.latestAnalysisId ?? undefined
        );
        dispatch(setSpatialState({ ...spatialData }));
        dispatch(setGlobalAdminArea(configResult.globalAdminAreaData));
      }
    }
  );

  return {
    mutateSaveConfig: handleSaveConfiguration,
    mutateOpenConfig,
    mutateGetAnalysisConfig,
    configurationDetailData // TODO: to be deleted after refactor
  };
};
